import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './scheduling-components.css';

interface OptimizationResult {
  optimizationId: string;
  recommendations: {
    department: string;
    currentStaffing: number;
    recommendedStaffing: number;
    reason: string;
    potentialSavings: number;
  }[];
  totalSavings: number;
  createdAt: string;
}

interface ShiftOptimizationProps {
  storeId: string;
  enterpriseId: string;
}

export const ShiftOptimization: React.FC<ShiftOptimizationProps> = ({ 
  storeId, 
  enterpriseId 
}) => {
  const [optimization, setOptimization] = useState<OptimizationResult | null>(null);
  const [generating, setGenerating] = useState(false);

  const generateOptimization = async () => {
    try {
      setGenerating(true);
      const response = await fetch(`${API_ENDPOINTS.employees()}/shift-optimization/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          storeId,
          enterpriseId,
          weekStart: new Date().toISOString().split('T')[0]
        })
      });

      if (response.ok) {
        const data = await response.json();
        setOptimization(data.data);
      }
    } catch (error) {
      console.error('Error generating optimization:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="shift-optimization">
      <div className="page-header">
        <h2>Shift Optimization</h2>
        <button 
          className="btn btn-primary"
          onClick={generateOptimization}
          disabled={generating}
        >
          {generating ? 'Generating...' : 'Generate Optimization'}
        </button>
      </div>

      {optimization ? (
        <div className="optimization-results">
          <div className="results-header">
            <h3>Optimization Results</h3>
            <div className="total-savings">
              <span className="label">Potential Weekly Savings:</span>
              <span className="amount">${optimization.totalSavings.toFixed(2)}</span>
            </div>
          </div>

          <div className="recommendations">
            {optimization.recommendations.map((rec, index) => (
              <div key={index} className="recommendation-card">
                <div className="rec-header">
                  <h4>{rec.department}</h4>
                  <span className="savings">${rec.potentialSavings.toFixed(2)} saved</span>
                </div>
                <div className="rec-details">
                  <div className="staffing-change">
                    <span className="current">Current: {rec.currentStaffing}</span>
                    <span className="arrow">→</span>
                    <span className="recommended">Recommended: {rec.recommendedStaffing}</span>
                  </div>
                  <p className="reason">{rec.reason}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <h3>AI Shift Optimization</h3>
          <p>Get intelligent recommendations to optimize your staffing and reduce labor costs.</p>
          <button 
            className="btn btn-primary"
            onClick={generateOptimization}
            disabled={generating}
          >
            {generating ? 'Generating Recommendations...' : 'Start Optimization'}
          </button>
        </div>
      )}
    </div>
  );
};